/**
 * /checkout_redirect — Stripe return leg for checkouts started in the mobile app.
 *
 * Rendered at the AppShell level (like /share/:id), outside the auth gate and the V2/V3 fork.
 * iOS: offer (and try once) vivideo://checkout_redirect?<same query>. Elsewhere: hand the
 * query to /upgrade, where the web's normal post-checkout handling runs.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '@/v2/i18n';

// iPadOS 13+ Safari reports a Mac UA; a touch-capable "Mac" is an iPad.
function isIOS(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export function CheckoutRedirectPage() {
  const { t } = useTranslation('v2');
  const query = window.location.search.replace(/^\?/, '');
  const ios = isIOS();
  const appUrl = `vivideo://checkout_redirect${query ? `?${query}` : ''}`;
  const webUrl = `/upgrade${query ? `?${query}` : ''}`;
  const triedRef = useRef(false);

  useEffect(() => {
    if (triedRef.current) return;
    triedRef.current = true;
    if (ios) window.location.href = appUrl;
    else window.location.replace(webUrl);
  }, [ios, appUrl, webUrl]);

  if (!ios) return null;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-2xl border border-glass-border bg-surface p-8 text-center">
        <h1 className="text-lg font-bold text-text-primary mb-5">{t('checkoutRedirect.title')}</h1>
        <a
          href={appUrl}
          className="block w-full px-4 py-3 rounded-xl bg-primary text-sm font-semibold text-white hover:opacity-90 transition-opacity"
        >
          {t('checkoutRedirect.openApp')}
        </a>
        <a href={webUrl} className="mt-4 inline-block text-sm text-text-tertiary underline">
          {t('checkoutRedirect.continueWeb')}
        </a>
      </div>
    </div>
  );
}
